import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronRight, Plus, Building2, User } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Opportunity } from '@/hooks/useOpportunities';
import { SCOPING_STAGES } from '@/hooks/useOpportunities';

interface ExpandableOpportunityRowProps {
  opportunity: Opportunity;
  onAddProject: (opportunityId: string) => void;
}

export function ExpandableOpportunityRow({
  opportunity,
  onAddProject,
}: ExpandableOpportunityRowProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const projects = opportunity.projects || [];
  const scopedValue = opportunity.scopedValue || opportunity.totalValue || 0;
  const unscopedValue = opportunity.unscopedValue || 0;
  const servicesValue = opportunity.servicesValue || 0;
  const projectCount = opportunity.projectCount || projects.length;

  return (
    <div className="border-b border-gray-200 last:border-b-0">
      {/* Main Row */}
      <div
        className={cn(
          'grid grid-cols-[auto_1fr_140px_120px_110px_110px_90px] items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors',
          isExpanded && 'bg-gray-50'
        )}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {/* Expand Toggle */}
        <div className="flex items-center">
          {projectCount > 0 ? (
            isExpanded ? (
              <ChevronDown className="h-4 w-4 text-gray-400" />
            ) : (
              <ChevronRight className="h-4 w-4 text-gray-400" />
            )
          ) : (
            <div className="w-4" />
          )}
        </div>

        {/* Customer & Title */}
        <div className="min-w-0">
          <Link
            to={`/opportunities/${opportunity.id}`}
            className="font-medium text-xtravirt-dark hover:text-xtravirt-cyan transition-colors"
            onClick={(e) => e.stopPropagation()}
          >
            {opportunity.title}
          </Link>
          <div className="flex items-center gap-2 mt-0.5 text-sm text-gray-500">
            <Building2 className="h-3 w-3" />
            <span className="truncate">{opportunity.customerName}</span>
          </div>
        </div>

        {/* Account Director */}
        <div className="min-w-0">
          {opportunity.accountDirectorName ? (
            <div className="flex items-center gap-1.5 text-sm text-gray-700">
              <User className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
              <span className="truncate">{opportunity.accountDirectorName}</span>
            </div>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </div>

        {/* Funding Type */}
        <div className="min-w-0">
          {opportunity.fundingTypeName ? (
            <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 truncate max-w-full">
              {opportunity.fundingTypeName}
            </span>
          ) : (
            <span className="text-xs text-gray-400">TBC</span>
          )}
        </div>

        {/* Scoped Value */}
        <div className="text-right">
          {scopedValue > 0 ? (
            <span className="text-sm font-semibold text-green-700">
              {formatCurrency(scopedValue, opportunity.currency)}
            </span>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </div>

        {/* Unscoped Value */}
        <div className="text-right">
          {unscopedValue > 0 ? (
            <span className="text-sm font-medium text-amber-600">
              {formatCurrency(unscopedValue, opportunity.currency)}
            </span>
          ) : servicesValue > 0 && scopedValue === 0 ? (
            <span className="text-sm font-medium text-amber-600">
              {formatCurrency(servicesValue, opportunity.currency)}
            </span>
          ) : (
            <span className="text-xs text-gray-400">—</span>
          )}
        </div>

        {/* Project Count with stage pills */}
        <div className="text-center">
          <span className="inline-flex items-center justify-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
            {projectCount}
          </span>
        </div>
      </div>

      {/* Expanded Content - Projects with individual stages */}
      {isExpanded && (
        <div className="bg-gray-50 border-t border-gray-100 px-4 py-3 pl-12">
          {projects.length > 0 ? (
            <div className="space-y-2">
              {projects.map((project) => {
                const stageInfo = SCOPING_STAGES.find((s) => s.id === project.currentScopingStage);
                return (
                  <div key={project.id} className="flex items-center justify-between gap-3 bg-white rounded-lg border border-gray-200 px-3 py-2">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <Link
                        to={`/projects/${project.id}`}
                        className="text-sm font-medium text-xtravirt-dark hover:text-xtravirt-cyan truncate"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {project.name}
                      </Link>
                    </div>
                    {stageInfo && (
                      <span className={cn('inline-block px-2 py-0.5 rounded text-xs font-medium flex-shrink-0', stageInfo.color)}>
                        {stageInfo.name}
                      </span>
                    )}
                    {project.totalCost > 0 && (
                      <span className="text-sm font-medium text-green-700 flex-shrink-0">
                        {formatCurrency(project.totalCost)}
                      </span>
                    )}
                    {project.totalDays > 0 && (
                      <span className="text-xs text-gray-500 flex-shrink-0">
                        {(project.totalDays / 100).toFixed(1)}d
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-4 text-gray-500 text-sm">
              No projects yet
            </div>
          )}

          {/* Add Project Button */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              onAddProject(opportunity.id);
            }}
            className="mt-3 flex items-center gap-2 text-sm text-xtravirt-cyan hover:text-xtravirt-dark transition-colors"
          >
            <Plus className="h-4 w-4" />
            Add Project
          </button>
        </div>
      )}
    </div>
  );
}

function formatCurrency(cents: number, currency: 'GBP' | 'USD' | 'EUR' = 'USD'): string {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}
